import { hotelQuoteSurcharges } from './hotelReservationFlow';
import { HotelCancellation } from './HotelCancellation';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HotelReservation } from '@bandido/shared/types';

const KEY = 'hotelReservation';
const object = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
const scalar = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '—';

export function HotelQuoteTerms({
  reservation,
  previousPrice,
  reviewing = reservation.hotelSnapshot.phase === 'quoted',
}: {
  reservation: HotelReservation;
  previousPrice?: { currency: string; amount: string };
  reviewing?: boolean;
}) {
  const { t } = useTranslation();
  const quote = reservation.hotelSnapshot.quote;
  if (!quote) return null;
  const boardName = reservation.hotelSnapshot.rooms.find(
    (room) => room.board_basis === quote.providerBoardCode,
  )?.board_display_name;
  const terms = object(quote.commercialTerms);
  const price = object(terms.price);
  const policy = object(terms.cancelPolicy);
  const normalizedPolicy = object(quote.cancellationPolicy);
  const refundable = normalizedPolicy.refundable ?? policy.refundable;
  const normalizedWindows = (Array.isArray(normalizedPolicy.windows) ? normalizedPolicy.windows : []).filter(
    (value) => {
      const window = object(value);
      return [
        window.starts_at,
        window.ends_at,
        window.penalty_type,
        window.description,
        object(window.price).source_amount_text,
      ].some((detail) => typeof detail === 'string' && detail.trim().length > 0);
    },
  );
  const summary = typeof normalizedPolicy.summary === 'string' ? normalizedPolicy.summary.trim() : '';
  const description = typeof normalizedPolicy.description === 'string' ? normalizedPolicy.description.trim() : '';
  const penalties = Array.isArray(policy.cancelPenalties) ? policy.cancelPenalties : [];
  const confirmedPolicy = object(reservation.hotelSnapshot.confirmedCancellationPolicy);
  const confirmedWindows = Array.isArray(confirmedPolicy.windows) ? confirmedPolicy.windows : [];
  const reviewTitle = quote.priceChanged
    ? quote.cancellationChangedFromSearch === true
      ? 'priceAndCancellationChanged'
      : 'priceChanged'
    : quote.cancellationChangedFromSearch === true
      ? 'cancellationUpdated'
      : 'reviewConditions';
  return (
    <Stack spacing={1}>
      {reviewing ? (
        <Alert severity={quote.priceChanged || quote.cancellationChangedFromSearch === true ? 'warning' : 'info'}>
          <Typography variant="subtitle2">{t(`${KEY}.${reviewTitle}`)}</Typography>
          {t(`${KEY}.reviewBeforeBooking`)}
        </Alert>
      ) : (
        <Typography variant="subtitle2">{t(`${KEY}.quotationTerms`)}</Typography>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {reviewing && previousPrice && (
          <Stack sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t(`${KEY}.selectedPrice`)}
            </Typography>
            <Typography>
              {previousPrice.currency} {previousPrice.amount}
            </Typography>
          </Stack>
        )}
        <Stack sx={{ flex: 1 }}>
          {reviewing && (
            <Typography variant="body2" color="text.secondary">
              {t(`${KEY}.currentPrice`)}
            </Typography>
          )}
          <Typography variant="h6">
            {quote.price.currency_code} {quote.price.source_amount_text}
          </Typography>
        </Stack>
      </Stack>
      {Array.isArray(terms.rateRules) && terms.rateRules.length > 0 && (
        <Typography>
          {t('hotelSearch.restrictions')}: {terms.rateRules.map(scalar).join(', ')}
        </Typography>
      )}
      {Array.isArray(terms.rateRules) &&
        terms.rateRules.some((rule) => typeof rule === 'string' && rule.trim().toUpperCase() === 'PACKAGE') && (
          <Typography>{t(`${KEY}.packageCondition`)}</Typography>
        )}
      {quote.providerBoardCode && (
        <Typography>
          {t('hotelSearch.boardFilter')}: {boardName || `${t(`${KEY}.boardCode`)}: ${quote.providerBoardCode}`}
        </Typography>
      )}
      {reviewing && quote.expiresAt && <Typography>{t(`${KEY}.expires`, { date: quote.expiresAt })}</Typography>}
      {!!Object.keys(price).length && (
        <Typography variant="body2">
          {t(`${KEY}.priceComponents`, {
            net: scalar(price.net),
            gross: scalar(price.gross),
            msp: scalar(price.minimumSellingPrice),
          })}
          {price.binding === true && ` · ${t(`${KEY}.binding`)}`}
        </Typography>
      )}
      {quote.cancellation ? (
        <Stack spacing={1} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle2">{t(`${KEY}.cancellation`)}</Typography>
          <HotelCancellation
            key={JSON.stringify([
              reservation.id,
              reviewing,
              quote.acceptedQuote,
              quote.cancellationChangedFromSearch,
              quote.cancellation.periods,
              quote.cancellation.description,
            ])}
            policy={quote.cancellation}
            defaultDetailsOpen={reviewing && quote.cancellationChangedFromSearch === true}
          />
        </Stack>
      ) : (
        <>
          <Typography variant="subtitle2">{t(`${KEY}.cancellation`)}</Typography>
          {refundable === false && <Typography>{t(`${KEY}.nonRefundable`)}</Typography>}
          {summary && <Typography>{summary}</Typography>}
          {description && <Typography>{description}</Typography>}
          {!penalties.length && !normalizedWindows.length && !summary && !description && refundable !== false && (
            <Typography>{t(`${KEY}.policyUnknown`)}</Typography>
          )}
          {penalties.map((value, index) => {
            const penalty = object(value);
            return (
              <Typography key={index} variant="body2">
                {t(`${KEY}.penalty`, {
                  date: scalar(penalty.deadline),
                  value: scalar(penalty.value),
                  type: scalar(penalty.penaltyType),
                  currency: penalty.penaltyType === 'IMPORT' ? scalar(penalty.currency) : '',
                })}
              </Typography>
            );
          })}
          {!penalties.length &&
            normalizedWindows.map((value, index) => {
              const window = object(value);
              const amount = object(window.price);
              return (
                <Stack key={`quote-window:${index}`}>
                  <Typography variant="body2">
                    {t(`${KEY}.penaltyWindow`, {
                      startsAt: scalar(window.starts_at),
                      endsAt: scalar(window.ends_at),
                      value: scalar(amount.source_amount_text),
                      currency: typeof amount.currency_code === 'string' ? amount.currency_code : '',
                      type: scalar(window.penalty_type),
                    })}
                  </Typography>
                  {typeof window.description === 'string' && (
                    <Typography variant="body2">{window.description}</Typography>
                  )}
                </Stack>
              );
            })}
        </>
      )}
      {hotelQuoteSurcharges(quote).map((value, index) => (
        <Typography key={index} variant="body2">
          {scalar(value.description)} · {scalar(value.kind ?? value.chargeType ?? value.charge_type)} ·{' '}
          {scalar(object(value.price).currency_code ?? object(value.price).currency)}{' '}
          {scalar(object(value.price).source_amount_text ?? object(value.price).gross)} ·{' '}
          {t(
            value.included === true
              ? 'hotelSearch.chargeIncluded'
              : value.included === false
                ? 'hotelSearch.chargeExcluded'
                : 'hotelSearch.chargeUnknown',
          )}
        </Typography>
      ))}
      {reservation.hotelSnapshot.rooms.flatMap((room) =>
        (room.searchSurcharges ?? []).map((charge, index) => (
          <Typography key={`search:${room.room_index}:${index}`} variant="body2">
            {t(`${KEY}.searchRoomCharge`, { room: room.room_index + 1 })} · {charge.description ?? '—'}
            {charge.price ? ` · ${charge.price.currency} ${charge.price.amount}` : ''} ·{' '}
            {t(
              charge.included === true
                ? 'hotelSearch.chargeIncluded'
                : charge.included === false
                  ? 'hotelSearch.chargeExcluded'
                  : 'hotelSearch.chargeUnknown',
            )}
          </Typography>
        )),
      )}
      {quote.remarks.length > 0 && (
        <Box component="details" sx={{ py: 1 }}>
          <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main', fontSize: 14, py: 0.5 }}>
            {t(`${KEY}.providerObservations`)}
          </Box>
          <Stack spacing={1} sx={{ pt: 1 }}>
            {quote.remarks.map((remark, index) => (
              <Typography variant="body2" key={index} sx={{ whiteSpace: 'pre-line' }}>
                {remark}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
      <HotelNativeConditions groups={quote.nativeConditions} />
      {reservation.hotelSnapshot.phase === 'confirmed' && (
        <>
          <Typography variant="subtitle2">{t(`${KEY}.confirmedCancellation`)}</Typography>
          {confirmedPolicy.refundable === false && <Typography>{t(`${KEY}.nonRefundable`)}</Typography>}
          {typeof confirmedPolicy.summary === 'string' && <Typography>{confirmedPolicy.summary}</Typography>}
          {!confirmedWindows.length && confirmedPolicy.refundable !== false && !confirmedPolicy.summary && (
            <Typography>{t(`${KEY}.policyUnknown`)}</Typography>
          )}
          {confirmedWindows.map((value, index) => {
            const window = object(value);
            const price = object(window.price);
            return (
              <Typography key={`confirmed:${index}`} variant="body2">
                {t(`${KEY}.penalty`, {
                  date: scalar(window.deadline),
                  value: scalar(price.source_amount_text),
                  type: scalar(window.penalty_type),
                  currency: window.penalty_type === 'IMPORT' ? scalar(price.currency_code) : '',
                })}
              </Typography>
            );
          })}
        </>
      )}
    </Stack>
  );
}
import { HotelNativeConditions } from './HotelNativeConditions';
